/* eslint-disable no-restricted-syntax */

import { createSelector } from 'reselect';
import _ from 'lodash';
import Immutable from 'immutable';
import { selectType } from '../../../ReduxStorage/location/selectors';

import './injectReducers';
import { coursesSelector } from '../../../ReduxStorage/Courses/selectors';
import { selectInstitutions } from '../../../ReduxStorage/Institutions/selectors';
import { enrolmentsSelector } from '../../../ReduxStorage/Enrolments/selectors';
import { selectQualificationEnrolments } from '../../../ReduxStorage/QualificationEnrolments/selectors';
import { cohortsSelector } from '../../../ReduxStorage/Cohorts/selectors';
import { usersSelector } from '../../../ReduxStorage/Users/selectors';
import { ROUTE_HOMEPAGE } from '../../../ReduxStorage/routeActions';

export const selectTopbarCourseSearchString = (state) =>
  state.TopbarCourseSearchString;

const addCohort = (course, cohort, pathToCourse, _cohortsByCreator) => {
  let cohortsByCreator = _cohortsByCreator;
  const keyPath = pathToCourse;
  if (!cohortsByCreator.hasIn(keyPath)) {
    cohortsByCreator = cohortsByCreator.setIn(
      keyPath,
      Immutable.fromJS({
        course,
        cohorts: [],
        searchableStrings: [course.name],
      })
    );
  }
  const cohortsKeyPath = keyPath.concat(['cohorts']);
  let updatedCohorts = cohortsByCreator.getIn(cohortsKeyPath);
  updatedCohorts = updatedCohorts.push(Immutable.fromJS(cohort));
  cohortsByCreator = cohortsByCreator.setIn(cohortsKeyPath, updatedCohorts);

  return cohortsByCreator;
};

const sortByCohorts = (byCohorts) =>
  byCohorts.sort((a, b) => {
    return a.get('title').localeCompare(b.get('title'));
  });

const sortByCourses = (byCourses) => {
  return byCourses
    .sort((a, b) => {
      return a
        .getIn(['course', 'name'])
        .localeCompare(b.getIn(['course', 'name']));
    })
    .map((course) => {
      return course.set('cohorts', sortByCohorts(course.get('cohorts')));
    });
};

const sortByQualifications = (byQualifications) => {
  return byQualifications
    .sort((a, b) => {
      return a
        .getIn(['qualification', 'name'])
        .localeCompare(b.getIn(['qualification', 'name']));
    })
    .map((qualification) => {
      return qualification.set(
        'courses',
        sortByCourses(qualification.get('courses').toList())
      );
    });
};

const sortByCreators = (byCreators) => {
  return byCreators
    .sort((a, b) => {
      const getName = (item) => {
        if (item.has('institution')) {
          return item.getIn(['institution', 'name']);
        }
        return item.getIn(['creator', 'fullName']);
      };
      return getName(a).localeCompare(getName(b));
    })
    .map((createdBy) => {
      let innerSorted = createdBy;
      if (createdBy.has('qualifications')) {
        innerSorted = innerSorted.set(
          'qualifications',
          sortByQualifications(createdBy.get('qualifications').toList())
        );
      }
      if (createdBy.has('courses')) {
        innerSorted = innerSorted.set(
          'courses',
          sortByCourses(createdBy.get('courses').toList())
        );
      }
      return innerSorted;
    });
};

const getQualificationEnrolments = (
  qualificationEnrolments,
  institutionPath
) => {
  if (qualificationEnrolments.hasIn(['byInstitution', institutionPath])) {
    return qualificationEnrolments
      .getIn(['byInstitution', institutionPath])
      .map((qe) => {
        const qualification = qe.get('qualification');
        const searchableStrings = [qualification.get('title')];
        return Immutable.fromJS({
          qualification,
          courses: {},
          searchableStrings,
        });
      });
  }
  return Immutable.Map();
};

export const getEnrolmentsByCreator = (
  courses,
  institutions,
  enrolments,
  qualificationEnrolments,
  cohorts,
  users
) => {
  let byCreator = Immutable.Map();
  if (qualificationEnrolments.has('byInstitution')) {
    // students can be enrolled in a qualification, but none of its courses, so add these to the map first
    for (const institutionPath of qualificationEnrolments
      .get('byInstitution')
      .keys()) {
      if (institutions.has(institutionPath)) {
        const institution = institutions.get(institutionPath);
        byCreator = byCreator.set(
          institutionPath,
          Immutable.fromJS({
            institution,
            qualifications: getQualificationEnrolments(
              qualificationEnrolments,
              institution.get('path')
            ),
            courses: {},
            searchableStrings: [institution.get('name')],
          })
        );
      }
    }
  }

  byCreator = _.reduce(
    enrolments,
    (cohortsByCreatorMap, enrolment) => {
      if (!enrolment.display) {
        return cohortsByCreatorMap;
      }

      const course = courses.get(enrolment.coursePath).toJS();
      const cohort = cohorts.get(enrolment.cohortPath).toJS();
      const creator = Immutable.fromJS(users[course.createdBy]);
      let institution = null;
      if (course.institution && institutions.has(course.institution)) {
        institution = institutions.get(course.institution);
      }
      let cohortsByCreator = cohortsByCreatorMap;

      const createdBy = course.institution || course.createdBy;
      if (!cohortsByCreator.has(createdBy)) {
        if (institution) {
          cohortsByCreator = cohortsByCreator.set(
            createdBy,
            Immutable.fromJS({
              institution,
              qualifications: getQualificationEnrolments(
                qualificationEnrolments,
                institution.get('path')
              ),
              courses: {},
              searchableStrings: [institution.get('name')],
            })
          );
        } else {
          cohortsByCreator = cohortsByCreator.set(
            createdBy,
            Immutable.fromJS({
              creator,
              courses: {},
              searchableStrings: [
                creator.get('fullName'),
                creator.get('profileName'),
              ],
            })
          );
        }
      }

      let pathToCourse = [createdBy, 'courses', course.id];
      let addedUnderQualification = false;
      if (
        institution &&
        cohortsByCreator.getIn([createdBy, 'qualifications']).size > 0
      ) {
        // check if this course is in any qualifications
        for (const [qualificationId, qEnrolment] of cohortsByCreator
          .getIn([createdBy, 'qualifications'])
          .entries()) {
          const qualification = qEnrolment.get('qualification');
          if (qualification.get('coreCourses').contains(enrolment.coursePath)) {
            pathToCourse = [
              createdBy,
              'qualifications',
              qualificationId,
              'courses',
              course.id,
            ];
            cohortsByCreator = addCohort(
              course,
              cohort,
              pathToCourse,
              cohortsByCreator
            );
            addedUnderQualification = true;
          }
        }
      }
      if (!addedUnderQualification) {
        cohortsByCreator = addCohort(
          course,
          cohort,
          pathToCourse,
          cohortsByCreator
        );
      }

      return cohortsByCreator;
    },
    byCreator
  );

  return sortByCreators(byCreator.toList());
};

const selectCreatedBy = createSelector(
  coursesSelector,
  selectInstitutions,
  enrolmentsSelector,
  selectQualificationEnrolments,
  cohortsSelector,
  usersSelector,
  (
    courses,
    institutions,
    enrolments,
    qualificationEnrolments,
    cohorts,
    users
  ) => {
    return getEnrolmentsByCreator(
      courses,
      institutions,
      enrolments.toJS(),
      qualificationEnrolments,
      cohorts,
      users.toJS()
    );
  }
);

const matches = (searchableStrings, searchString) => {
  return searchableStrings.find((searchableString) => {
    return (
      searchableString.toLowerCase().indexOf(searchString.toLowerCase()) !== -1
    );
  });
};

const filterByCourses = (byCourses, searchString) => {
  return byCourses
    .map((byCourse) => {
      if (matches(byCourse.get('searchableStrings'), searchString)) {
        return byCourse;
      }
      return null;
    })
    .filter((byCourse) => byCourse !== null);
};

const filterByQualifications = (byQualifications, searchString) => {
  return byQualifications
    .map((byQualification) => {
      if (matches(byQualification.get('searchableStrings'), searchString)) {
        return byQualification;
      }

      const filteredCourses = filterByCourses(
        byQualification.get('courses'),
        searchString
      );
      if (filteredCourses.size > 0) {
        return byQualification.set('courses', filteredCourses);
      }

      return null;
    })
    .filter((byQualification) => byQualification !== null);
};

const filterByCreators = (byCreators, searchString) => {
  return byCreators
    .map((createdBy) => {
      if (matches(createdBy.get('searchableStrings'), searchString)) {
        return createdBy;
      }

      let matchesChildren = false;
      let filtered = createdBy;
      if (filtered.has('qualifications')) {
        filtered = filtered.set(
          'qualifications',
          filterByQualifications(filtered.get('qualifications'), searchString)
        );
        matchesChildren =
          matchesChildren || filtered.get('qualifications').size;
      }
      if (filtered.has('courses')) {
        filtered = filtered.set(
          'courses',
          filterByCourses(filtered.get('courses'), searchString)
        );
        matchesChildren = matchesChildren || filtered.get('courses').size;
      }

      return matchesChildren ? filtered : null;
    })
    .filter((byCreator) => byCreator !== null);
};

export const selectFilteredCohortsByCreatorOrInstitution = createSelector(
  selectCreatedBy,
  selectTopbarCourseSearchString,
  (createdBy, searchString) => {
    let filteredMenu = createdBy;
    if (searchString) {
      filteredMenu = filterByCreators(createdBy, searchString);
    }

    return filteredMenu.toJS();
  }
);

export const selectShowForInstitutionsLink = createSelector(
  [selectType],
  (type) => {
    return type === ROUTE_HOMEPAGE;
  }
);
